
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useBookingFormInit } from "./useBookingFormInit";
import { useClassDetails } from "./useClassDetails";
import { useGroupBooking } from "./useGroupBooking";
import { useBookingSubmission } from "./useBookingSubmission";
import { bookingSchema, FormValues } from "../types";
import { useAuth } from "@/lib/hooks/auth";
import { useUserSubscriptions } from "./useUserSubscriptions";

export const useBookingForm = (verifiedGiftCardId: string | null) => {
  const [searchParams] = useSearchParams();
  // Support both classId and class parameters for backward compatibility
  const classId = searchParams.get("classId") || searchParams.get("class") || "";
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // User subscriptions
  const { userSubscriptions } = useUserSubscriptions();
  
  // Form initialization
  const form = useForm<FormValues>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      isGroupBooking: false,
      groupSize: 2,
      useGiftCard: false,
      giftCardCode: "",
      createAccount: false,
      password: "",
      useSubscription: "pay_individually",
    },
  });
  
  // Track form values that we need to respond to
  const isGroupBooking = form.watch("isGroupBooking");
  const groupSize = form.watch("groupSize");
  const useGiftCard = form.watch("useGiftCard");
  const selectedSubscription = form.watch("useSubscription");
  
  // Load class details
  const { classDetails, isClassLoading } = useClassDetails(classId);
  
  // Initialize form with user data if available
  useBookingFormInit();
  
  // Group booking functionality
  const { groupNameInputs, updateGroupName } = useGroupBooking(groupSize);
  
  // Form submission
  const { 
    isSubmitting, 
    bookingSuccess, 
    createdUserId, 
    verificationEmailSent,
    bookingId,
    paymentStatus,
    onSubmit: handleSubmit 
  } = useBookingSubmission(classId, verifiedGiftCardId, groupNameInputs);
  
  // Redirect if no classId
  useEffect(() => {
    if (!classId) {
      navigate("/classes");
    }
  }, [classId, navigate]);
  
  // Form submission handler
  const onSubmit = (values: FormValues) => {
    handleSubmit(values, classDetails, userSubscriptions);
  };

  return {
    form,
    classDetails,
    isClassLoading,
    bookingSuccess,
    createdUserId,
    verificationEmailSent,
    isSubmitting,
    selectedSubscription,
    isGroupBooking,
    groupSize,
    useGiftCard,
    groupNameInputs,
    updateGroupName,
    onSubmit,
    bookingId,
    paymentStatus
  };
};
